import React, { useEffect, useMemo, useState } from "react";
import { adminApi } from "../../utils/axiosConfig";
import * as XLSX from "xlsx";

export default function AdminQuickOrders() {
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const res = await adminApi.get(`/api/admin/quick-orders`, {
        params: { page, limit, status: statusFilter || undefined, search: search || undefined },
      });
      const payload = res.data || {};
      setOrders(payload.items || []);
      setTotal(payload.total || 0);
    } catch (e) {
      setError("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [page, limit, statusFilter, search]);

  const updateStatus = async (id, status) => {
    try {
      await adminApi.put(`/api/admin/quick-orders/${id}/status`, { status });
      fetchOrders();
    } catch (e) {}
  };

  const generateInvoice = (o) => {
    const total = ((o.price || 0) * (o.quantity || 1)).toFixed(2);
    const dateStr = new Date(o.createdAt).toLocaleString();
    const html = `<!doctype html>
    <html>
      <head>
        <meta charset="utf-8" />
        <title>Facture - ${o._id}</title>
        <style>
          body { font-family: 'Georgia', serif; color:#1f2937; padding:24px; }
          .header { display:flex; justify-content:space-between; align-items:center; border-bottom:1px solid #e5e7eb; padding-bottom:12px; margin-bottom:16px; }
          .brand { font-weight:700; font-size:20px; color:#14532d; }
          .muted { color:#6b7280; font-size:12px; }
          .section { margin: 16px 0; }
          .card { border:1px solid #e5e7eb; border-radius:10px; padding:14px; background:#fff; }
          .row { display:flex; gap:16px; flex-wrap:wrap; }
          .col { flex:1 1 260px; }
          table { width:100%; border-collapse:collapse; }
          th, td { text-align:left; padding:10px; border-bottom:1px solid #f1f5f9; }
          th { background:#f8fafc; }
          .total { font-weight:700; }
          .footer { margin-top:24px; padding-top:12px; border-top:1px solid #e5e7eb; text-align:center; color:#64748b; font-size:12px; }
        </style>
      </head>
      <body>
        <div class="header">
          <div>
            <div class="brand">SAFA Store – Facture</div>
            <div class="muted">Essayez d’abord, Payez après</div>
          </div>
          <div class="muted">
            N° facture: ${o._id}<br/>
            Date: ${dateStr}
          </div>
        </div>
        <div class="section row">
          <div class="col card">
            <strong>Vendu par</strong>
            <div>SAFA Store</div>
            <div>support@safa-store.ma</div>
          </div>
          <div class="col card">
            <strong>Client</strong>
            <div>${o.fullName || ''}</div>
            <div>${o.phone || ''}</div>
            <div>${o.city || ''}</div>
            <div>${o.address || ''}</div>
          </div>
        </div>
        <div class="section card">
          <table>
            <thead>
              <tr>
                <th>Produit</th>
                <th>Variante</th>
                <th>Qté</th>
                <th>PU (MAD)</th>
                <th>Total (MAD)</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>${o.name}</td>
                <td>${o.couleur} / ${o.taille}</td>
                <td>${o.quantity || 1}</td>
                <td>${Number(o.price || 0).toFixed(2)}</td>
                <td>${total}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="section card" style="display:flex; justify-content:flex-end">
          <div style="min-width:240px;">
            <div style="display:flex; justify-content:space-between; padding:6px 0;"><span>Sous-total</span><span>${total} MAD</span></div>
            <div style="display:flex; justify-content:space-between; padding:6px 0;"><span>Livraison</span><span>Gratuit</span></div>
            <div style="height:1px; background:#e5e7eb; margin:8px 0;"></div>
            <div class="total" style="display:flex; justify-content:space-between; padding:6px 0;"><span>Total</span><span>${total} MAD</span></div>
          </div>
        </div>
        <div class="section card">
          <strong>Statut</strong>: ${statusLabel[o.status] || o.status}<br/>
          <strong>Commentaire</strong>: ${o.comment || ''}
        </div>
        <div class="footer">
          © ${new Date().getFullYear()} SAFA Store — Tous droits réservés. Merci pour votre confiance.
        </div>
        <script>window.onload = () => { window.print(); }</script>
      </body>
    </html>`;
    const w = window.open("", "_blank");
    w.document.write(html);
    w.document.close();
  };

  const shareQuickOrderWhatsApp = (o) => {
    const dateStr = new Date(o.createdAt).toLocaleString();
    const qty = Number(o.quantity || 1);
    const unit = Number(o.price || 0);
    const total = (unit * qty).toFixed(2);
    const header = `SAFA Store — Commande en 1 clic #${String(o._id).slice(-8)}\nDate: ${dateStr}`;
    const produit = `- ${o.name || ''} | ${o.couleur || ''}/${o.taille || ''} | ${qty} x ${unit.toFixed(2)} MAD = ${total} MAD`;
    const client = `${o.fullName || ''} | ${o.phone || ''}\n${o.address || ''} - ${o.city || ''}`;
    const statut = o.status || '';
    const commentaire = o.comment || '';
    const body = `Client:\n${client}\n\nArticle:\n${produit}\n\nTotal: ${total} MAD\nStatut: ${statut}\nCommentaire: ${commentaire}`;
    const text = `${header}\n\n${body}`;
    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank");
  };

  const statuses = ["nouvelle", "contacte", "confirmee", "annulee"];
  const statusLabel = {
    nouvelle: "Nouvelle",
    contacte: "Contacté",
    confirmee: "Confirmée",
    annulee: "Annulée",
  };
  const statusStyle = (active) => ({
    padding: "6px 12px",
    borderRadius: 18,
    border: `1px solid ${active ? "#15803d" : "#86efac"}`,
    background: active ? "#16a34a" : "#ffffff",
    color: active ? "#ffffff" : "#166534",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 700,
    boxShadow: active ? "0 2px 6px rgba(22,163,74,0.25)" : "none",
    transition: "all .2s ease",
  });

  const glassBtnStyle = (primary = false) => ({
    padding: "8px 12px",
    borderRadius: 12,
    border: primary ? "1px solid #16a34a" : "1px solid #86efac",
    background: primary ? "linear-gradient(180deg,#22c55e,#16a34a)" : "rgba(22,163,74,0.08)",
    color: primary ? "#ffffff" : "#166534",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 700,
    boxShadow: primary ? "0 8px 24px rgba(22,163,74,0.25)" : "0 6px 18px rgba(22,163,74,0.12)",
    backdropFilter: "blur(6px)",
    WebkitBackdropFilter: "blur(6px)",
    transition: "all .2s ease",
  });

  if (loading) return <div style={{ padding: 20 }}>Chargement...</div>;
  if (error) return <div style={{ padding: 20, color: "red" }}>{error}</div>;

  return (
    <div style={{ padding: 20 }}>
      <h2>Commandes en 1 clic</h2>
      <div style={{ display: "flex", gap: 10, alignItems: "center", margin: "12px 0" }}>
        <input
          value={search}
          onChange={(e) => { setPage(1); setSearch(e.target.value); }}
          placeholder="Rechercher (nom, tel, ville, adresse, taille, couleur)"
          style={{ padding: 8, border: "1px solid #e5e7eb", borderRadius: 8, minWidth: 280 }}
        />
        <select value={statusFilter} onChange={(e) => { setPage(1); setStatusFilter(e.target.value); }} style={{ ...glassBtnStyle(false) }}>
          <option value="">Tous les statuts</option>
          {statuses.map((s) => (
            <option key={s} value={s}>{statusLabel[s]}</option>
          ))}
        </select>
        <select value={limit} onChange={(e) => { setPage(1); setLimit(parseInt(e.target.value, 10)); }} style={{ ...glassBtnStyle(false) }}>
          {[10,20,50,100].map(n => <option key={n} value={n}>{n}/page</option>)}
        </select>
        <button
          type="button"
          onClick={async () => {
            const res = await adminApi.get(`/api/admin/quick-orders`, { params: { all: true, status: statusFilter || undefined, search: search || undefined } });
            const items = (res.data?.items || []).map(o => ({
              Date: new Date(o.createdAt).toLocaleString(),
              Produit: o.name,
              Couleur: o.couleur,
              Taille: o.taille,
              Quantite: o.quantity || 1,
              Total_MAD: ((o.price||0)*(o.quantity||1)).toFixed(2),
              Client: o.fullName,
              Telephone: o.phone,
              Ville: o.city || "",
              Adresse: o.address || "",
              Statut: statusLabel[o.status] || o.status,
              Commentaire: o.comment || "",
            }));
            const wb = XLSX.utils.book_new();
            const ws = XLSX.utils.json_to_sheet(items);
            XLSX.utils.book_append_sheet(wb, ws, 'QuickOrders');
            XLSX.writeFile(wb, 'quick-orders.xlsx');
          }}
          style={glassBtnStyle(true)}
        >
          Export Excel
        </button>
        <button
          type="button"
          onClick={async () => {
            const res = await adminApi.get(`/api/admin/quick-orders`, { params: { all: true, status: statusFilter || undefined, search: search || undefined } });
            const items = res.data?.items || [];
            const html = `<!doctype html><html><head><meta charset="utf-8"><title>Quick Orders</title></head><body>${
              `<h3>Commandes en 1 clic</h3>` +
              `<table border="1" cellpadding="6" cellspacing="0">` +
              `<thead><tr><th>Date</th><th>Produit</th><th>Couleur</th><th>Taille</th><th>Quantité</th><th>Total (MAD)</th><th>Client</th><th>Téléphone</th><th>Ville</th><th>Adresse</th><th>Statut</th><th>Commentaire</th></tr></thead>` +
              `<tbody>` + items.map(o => (
                `<tr>`+
                `<td>${new Date(o.createdAt).toLocaleString()}</td>`+
                `<td>${o.name}</td>`+
                `<td>${o.couleur}</td>`+
                `<td>${o.taille}</td>`+
                `<td>${o.quantity||1}</td>`+
                `<td>${((o.price||0)*(o.quantity||1)).toFixed(2)}</td>`+
                `<td>${o.fullName}</td>`+
                `<td>${o.phone}</td>`+
                `<td>${o.city||""}</td>`+
                `<td>${o.address||""}</td>`+
                `<td>${statusLabel[o.status] || o.status}</td>`+
                `<td>${o.comment||""}</td>`+
                `</tr>`
              )).join("") + `</tbody></table>`
            }`;
            const newWindow = window.open("", "_blank");
            newWindow.document.write(html);
            newWindow.document.close();
            newWindow.focus();
            newWindow.print();
          }}
          style={glassBtnStyle(true)}
        >
          Export PDF
        </button>
      </div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={{ textAlign: "left", borderBottom: "1px solid #ddd", padding: 8 }}>Date</th>
              <th style={{ textAlign: "left", borderBottom: "1px solid #ddd", padding: 8 }}>Produit</th>
              <th style={{ textAlign: "left", borderBottom: "1px solid #ddd", padding: 8 }}>Variante</th>
              <th style={{ textAlign: "left", borderBottom: "1px solid #ddd", padding: 8 }}>Qté</th>
              <th style={{ textAlign: "left", borderBottom: "1px solid #ddd", padding: 8 }}>Total</th>
              <th style={{ textAlign: "left", borderBottom: "1px solid #ddd", padding: 8 }}>Client</th>
              <th style={{ textAlign: "left", borderBottom: "1px solid #ddd", padding: 8 }}>Téléphone</th>
              <th style={{ textAlign: "left", borderBottom: "1px solid #ddd", padding: 8 }}>Ville</th>
              <th style={{ textAlign: "left", borderBottom: "1px solid #ddd", padding: 8 }}>Adresse</th>
              <th style={{ textAlign: "left", borderBottom: "1px solid #ddd", padding: 8 }}>Statut</th>
              <th style={{ textAlign: "left", borderBottom: "1px solid #ddd", padding: 8 }}>Commentaire</th>
              <th style={{ textAlign: "left", borderBottom: "1px solid #ddd", padding: 8 }}>Appeler/Facture/Partager</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((o) => (
              <tr key={o._id}>
                <td style={{ padding: 8 }}>{new Date(o.createdAt).toLocaleString()}</td>
                <td style={{ padding: 8 }}>{o.name}</td>
                <td style={{ padding: 8 }}>{o.couleur} / {o.taille}</td>
                <td style={{ padding: 8 }}>{o.quantity || 1}</td>
                <td style={{ padding: 8 }}>{((o.price || 0) * (o.quantity || 1)).toFixed(2)} MAD</td>
                <td style={{ padding: 8 }}>{o.fullName}</td>
                <td style={{ padding: 8 }}>{o.phone}</td>
                <td style={{ padding: 8 }}>{o.city || ""}</td>
                <td style={{ padding: 8, maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={o.address || ""}>
                  {o.address || ""}
                </td>
                <td style={{ padding: 8 }}>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {statuses.map((s) => (
                      <button
                        key={s}
                        type="button"
                        style={statusStyle(o.status === s)}
                        onClick={() => updateStatus(o._id, s)}
                      >
                        {statusLabel[s]}
                      </button>
                    ))}
                  </div>
                </td>
                <td style={{ padding: 8 }}>
                  <textarea
                    defaultValue={o.comment || ""}
                    onBlur={async (e) => {
                      const val = e.target.value;
                      try { await adminApi.put(`/api/admin/quick-orders/${o._id}/comment`, { comment: val }); } catch {}
                    }}
                    placeholder="Ajouter un commentaire..."
                    rows={2}
                    style={{ width: 240, padding: 8, border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 12 }}
                  />
                </td>
                <td style={{ padding: 8 }}>
                  <div style={{ display: "flex", gap: 8 }}>
                    <a href={`tel:${o.phone}`} title={`Appeler ${o.phone}`} style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 36,
                      height: 36,
                      borderRadius: "50%",
                      background: "#16a34a",
                      color: "#fff",
                      textDecoration: "none",
                      boxShadow: "0 2px 6px rgba(22,163,74,0.25)",
                    }}>
                      📞
                    </a>
                    <button type="button" onClick={() => generateInvoice(o)} title="Générer facture PDF" style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: "8px 12px",
                      borderRadius: 12,
                      border: "1px solid #16a34a",
                      background: "linear-gradient(180deg,#22c55e,#16a34a)",
                      color: "#fff",
                      fontWeight: 700,
                      boxShadow: "0 8px 24px rgba(22,163,74,0.25)",
                      cursor: "pointer",
                    }}>
                      Facture PDF
                    </button>
                    <button type="button" onClick={() => shareQuickOrderWhatsApp(o)} title="Partager sur WhatsApp" style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: "8px 12px",
                      borderRadius: 12,
                      border: "1px solid #86efac",
                      background: "rgba(22,163,74,0.08)",
                      color: "#166534",
                      fontWeight: 700,
                      boxShadow: "0 6px 18px rgba(22,163,74,0.12)",
                      cursor: "pointer",
                    }}>
                      Partager
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12 }}>
        <div style={{ color: "#6b7280", fontSize: 12 }}>
          {orders.length} sur {total} éléments — page {page}
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <button disabled={page<=1} onClick={() => setPage((p)=>Math.max(1,p-1))} style={glassBtnStyle(false)}>Précédent</button>
          <button disabled={(page*limit)>=total} onClick={() => setPage((p)=>p+1)} style={glassBtnStyle(false)}>Suivant</button>
        </div>
      </div>
    </div>
  );
}


